import React, { useState, useMemo } from 'react';
import {
  HOLLANDER_INTERCHANGE_DB,
  searchHollanderDatabase,
  getHollanderCategories,
} from '../../utils/hollanderEngine';
import { HollanderInterchangeEntry } from '../../types/bom';
import { MechanicalButton } from '../common/MechanicalButton';
import {
  Search,
  BookOpen,
  Car,
  X,
  Check,
  Tag,
  AlertCircle,
  FileSpreadsheet,
  ArrowRight,
} from 'lucide-react';

export interface HollanderLookupBarProps {
  onSelectEntry?: (entry: HollanderInterchangeEntry) => void;
  isOpen?: boolean;
  onClose?: () => void;
  inline?: boolean;
  initialQuery?: string;
  className?: string;
}

export const HollanderLookupBar: React.FC<HollanderLookupBarProps> = ({
  onSelectEntry,
  isOpen = true,
  onClose,
  inline = false,
  initialQuery = '',
  className = '',
}) => {
  const [query, setQuery] = useState(initialQuery);
  const [selectedCategory, setSelectedCategory] = useState<string>('ALL');
  const [selectedEntry, setSelectedEntry] = useState<HollanderInterchangeEntry | null>(
    HOLLANDER_INTERCHANGE_DB[0] || null
  );
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const categories = useMemo(() => ['ALL', ...getHollanderCategories()], []);

  const results = useMemo(() => {
    return searchHollanderDatabase(query, selectedCategory);
  }, [query, selectedCategory]);

  const handleCopyNumber = (hollanderNumber: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(hollanderNumber);
    setCopiedId(hollanderNumber);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleSelect = (entry: HollanderInterchangeEntry) => {
    setSelectedEntry(entry);
    if (onSelectEntry) {
      onSelectEntry(entry);
    }
  };

  if (!isOpen && !inline) return null;

  const content = (
    <div className={`space-y-4 text-shop-light ${className}`}>
      {/* Top Search Controls Bar */}
      <div className="flex flex-col sm:flex-row gap-3 items-stretch">
        {/* Search Input Box */}
        <div className="relative flex-1">
          <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-shop-muted">
            <Search className="w-4 h-4 text-safety-amber" />
          </div>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search Hollander #, Saginaw, 12-bolt, Chevelle, Granada, disc brakes..."
            className="w-full min-h-[44px] pl-10 pr-11 py-2.5 bg-shop-darkest border border-shop-border rounded text-sm text-shop-highlight placeholder-shop-muted focus:outline-none focus:border-safety-amber font-mono"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="absolute inset-y-0 right-0 min-h-[44px] min-w-[44px] flex items-center justify-center text-shop-muted hover:text-shop-highlight cursor-pointer"
              aria-label="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Category Dropdown */}
        <div className="sm:w-60">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-xs font-mono text-shop-light focus:outline-none focus:border-safety-amber cursor-pointer"
          >
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {cat === 'ALL' ? 'ALL CATEGORIES' : cat.toUpperCase()}
              </option>
            ))}
          </select>
        </div>

        {/* Clear / Reset Filter Button */}
        {(query || selectedCategory !== 'ALL') && (
          <MechanicalButton
            variant="outline"
            size="sm"
            onClick={() => {
              setQuery('');
              setSelectedCategory('ALL');
            }}
          >
            Reset
          </MechanicalButton>
        )}
      </div>

      {/* Main Results / Details Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Left Results List (5 columns) */}
        <div className="lg:col-span-5 space-y-2 max-h-[500px] overflow-y-auto pr-1">
          <div className="flex items-center justify-between text-xs font-mono text-shop-muted px-1">
            <span>INTERCHANGE MATCHES</span>
            <span className="font-bold text-safety-amber">{results.length} ENTRIES</span>
          </div>

          {results.length === 0 ? (
            <div className="p-8 text-center bg-shop-darkest border border-dashed border-shop-border rounded space-y-2">
              <AlertCircle className="w-8 h-8 text-shop-muted mx-auto" />
              <p className="text-xs text-shop-muted font-mono">
                No Hollander interchange records match &quot;{query}&quot;.
              </p>
              <button
                type="button"
                onClick={() => {
                  setQuery('');
                  setSelectedCategory('ALL');
                }}
                className="min-h-[44px] px-3 inline-flex items-center text-xs font-mono text-safety-amber underline hover:text-safety-amberDark cursor-pointer"
              >
                Clear search filters
              </button>
            </div>
          ) : (
            results.map((entry) => {
              const isSelected = selectedEntry?.hollanderNumber === entry.hollanderNumber;
              return (
                <div
                  key={entry.hollanderNumber}
                  onClick={() => handleSelect(entry)}
                  className={`p-3 rounded border text-left cursor-pointer transition-all duration-100 select-none ${
                    isSelected
                      ? 'bg-shop-panel border-safety-amber shadow-[inset_0_2px_0_0_#f59e0b]'
                      : 'bg-shop-darkest border-shop-border hover:bg-shop-dark hover:border-shop-steel'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1.5">
                    <span className="font-mono text-xs font-bold text-safety-amber bg-shop-darkest px-2 py-0.5 rounded border border-shop-border">
                      {entry.hollanderNumber}
                    </span>
                    <span className="text-[10px] font-mono text-blueprint-cyan bg-shop-panel px-1.5 py-0.5 rounded border border-shop-border truncate max-w-[140px]">
                      {entry.category}
                    </span>
                  </div>

                  <h5 className="text-xs font-bold text-shop-highlight line-clamp-2 uppercase">
                    {entry.description}
                  </h5>

                  <div className="mt-2 flex items-center justify-between text-[11px] font-mono text-shop-muted">
                    <span className="flex items-center gap-1">
                      <Car className="w-3 h-3 text-shop-steel" />
                      {entry.compatibleVehicles.length} Donor Models
                    </span>
                    <span className="text-shop-steel">{entry.yearSpan}</span>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Right Detail Pane (7 columns) */}
        <div className="lg:col-span-7 bg-shop-darkest border border-shop-border rounded p-4 flex flex-col justify-between">
          {selectedEntry ? (
            <div className="space-y-4">
              {/* Header Box */}
              <div className="border-b border-shop-border pb-3">
                <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-bold text-safety-amber bg-shop-panel px-2.5 py-1 rounded border border-safety-amberDark shadow-sm">
                      HOLLANDER #{selectedEntry.hollanderNumber}
                    </span>
                    <span className="text-xs font-mono font-bold text-shop-light bg-shop-well px-2 py-1 rounded border border-shop-border">
                      {selectedEntry.yearSpan}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => handleCopyNumber(selectedEntry.hollanderNumber, e)}
                      className="min-h-[44px] min-w-[44px] px-3 py-1.5 rounded bg-shop-panel border border-shop-border text-xs font-mono text-shop-highlight hover:border-shop-steel flex items-center gap-1.5 cursor-pointer"
                      title="Copy Hollander number to clipboard"
                    >
                      {copiedId === selectedEntry.hollanderNumber ? (
                        <>
                          <Check className="w-3.5 h-3.5 text-safety-green" />
                          <span className="text-safety-green font-bold">COPIED</span>
                        </>
                      ) : (
                        <>
                          <FileSpreadsheet className="w-3.5 h-3.5" />
                          <span>COPY #</span>
                        </>
                      )}
                    </button>

                    {onSelectEntry && (
                      <MechanicalButton
                        variant="primary"
                        size="sm"
                        icon={<ArrowRight className="w-4 h-4" />}
                        onClick={() => onSelectEntry(selectedEntry)}
                      >
                        Apply To Part
                      </MechanicalButton>
                    )}
                  </div>
                </div>

                <h4 className="text-base font-bold text-shop-highlight uppercase tracking-wide">
                  {selectedEntry.description}
                </h4>
                <div className="text-xs font-mono text-shop-muted mt-1 flex items-center gap-2">
                  <Tag className="w-3.5 h-3.5 text-safety-amber" />
                  Category: <strong className="text-shop-light">{selectedEntry.category}</strong>
                </div>
              </div>

              {/* Interchange Fitment Notes */}
              <div className="space-y-1.5">
                <span className="text-xs font-mono font-bold text-safety-amber uppercase tracking-wider block">
                  Engineering Fitment &amp; Swap Invariants:
                </span>
                <p className="text-xs text-shop-light leading-relaxed bg-shop-panel p-3 rounded border border-shop-border font-sans">
                  {selectedEntry.interchangeNotes}
                </p>
              </div>

              {/* Compatible Donor Vehicles List */}
              <div className="space-y-2">
                <span className="text-xs font-mono font-bold text-blueprint-cyan uppercase tracking-wider flex items-center gap-1.5">
                  <Car className="w-3.5 h-3.5" />
                  Compatible Factory Donor Platforms ({selectedEntry.compatibleVehicles.length}):
                </span>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-[160px] overflow-y-auto pr-1">
                  {selectedEntry.compatibleVehicles.map((vehicle, idx) => (
                    <div
                      key={idx}
                      className="px-2.5 py-1.5 bg-shop-panel rounded border border-shop-border text-xs text-shop-light font-mono truncate"
                      title={vehicle}
                    >
                      • {vehicle}
                    </div>
                  ))}
                </div>
              </div>

              {/* Cross-Reference OEM Part Numbers */}
              {selectedEntry.crossReferenceOEMs && selectedEntry.crossReferenceOEMs.length > 0 && (
                <div className="space-y-1.5 pt-2 border-t border-shop-border">
                  <span className="text-xs font-mono text-shop-muted uppercase block">
                    Factory OEM &amp; Aftermarket Cross-References:
                  </span>
                  <div className="flex flex-wrap gap-2">
                    {selectedEntry.crossReferenceOEMs.map((oem, idx) => (
                      <span
                        key={idx}
                        className="text-xs font-mono px-2 py-0.5 rounded bg-shop-well border border-shop-border text-shop-highlight"
                      >
                        {oem}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="p-8 text-center text-shop-muted font-mono text-xs">
              Select an interchange item from the list to view donor vehicles and fitment notes.
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (inline) {
    return content;
  }

  // Modal mode
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
      <div className="bg-shop-panel border-2 border-shop-steel rounded shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        {/* Modal Header */}
        <div className="px-5 py-4 bg-shop-darkest border-b border-shop-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="p-2 bg-shop-panel rounded border border-shop-border text-safety-amber">
              <BookOpen className="w-5 h-5" />
            </span>
            <div>
              <h3 className="text-base font-bold text-shop-highlight uppercase tracking-wider">
                Hollander Interchange &amp; Salvage Donor Directory
              </h3>
              <p className="text-xs text-shop-muted">
                Royal Hollander automotive salvage cross-reference indexing system
              </p>
            </div>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded bg-shop-dark border border-shop-border text-shop-muted hover:text-shop-highlight hover:border-shop-steel"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Modal Body */}
        <div className="p-5 overflow-y-auto flex-1">{content}</div>

        {/* Modal Footer */}
        <div className="px-5 py-3 bg-shop-darkest border-t border-shop-border flex justify-end">
          <MechanicalButton variant="secondary" size="sm" onClick={onClose}>
            Close Directory
          </MechanicalButton>
        </div>
      </div>
    </div>
  );
};
